import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Isvg from 'react-inlinesvg';
import { bem } from 'helpers/class-name';
import _isEmpty from 'lodash/isEmpty';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenue, selectRouteParams } from 'selectors/general';
import { selectLocale } from 'selectors/locale-data';
import Button from 'components/Button';
import { Card } from 'components/Card';
import Message from 'components/Message';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { MANAGE_SERVICES_TAB } from 'constants/logic';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';

import MissingObjectsCard from '../../Components/MissingObjectsCard/MissingObjectsCard';
import EmptyDetails from '../../EmptyDetails';
import { messages } from '../Services.messages';
import ServicesContext from '../ServicesContext';
import './ServiceDetails.scss';

const { block, element } = bem('service-details');

function ServiceDetails() {
  const navigate = useNavigate();

  const { currencyCode, venueId } = useSelector(selectActiveVenue);
  const routeParams = useSelector(selectRouteParams);
  const locale = useSelector(selectLocale);
  const { selectedService, isLoading } = useContext(ServicesContext);

  const isEmpty = _isEmpty(selectedService);
  const { name, duration, description, practitioners, resources, price } = selectedService || {};

  function handleEditClick() {
    const params = { ...routeParams, itemId: selectedService.id, focus: 'details' };
    const newPath = makePath.manageServicesEdit(params);

    navigate(newPath);
  }

  return (
    <Card className={block()} loading={isLoading} centered={isEmpty}>
      {isEmpty && <EmptyDetails text={messages.details.placeholder} />}
      {!isEmpty && (
        <div className={block('grid grid--flush')}>
          <div className={element('header-row', 'grid')}>
            <div data-cypress="service-details-header" className={element('header', 'grid__item 3/4 md-7/8')}>
              {name}
            </div>

            <Message
              as={Button}
              data-cypress="service-details-edit-button"
              className={element('edit-button', 'grid__item 1/4 md-1/8')}
              styled={false}
              onClick={handleEditClick}
              message={messages.details.edit}
            />

            <div className={element('duration-price', 'grid__item')}>
              <Message message={messages.details.duration} values={{ duration }} />

              <div className={element('vertical-divider')} />

              <When predicate={!price}>
                <div className={element('pending-review')}>
                  <Isvg className={element('pending-review-icon')} src={ICONS.clock} />
                  <Message message={messages.details.pendingReview} />
                </div>
              </When>

              <When predicate={!!price}>
                <Message className={element('price')} format="currency" value={price} currency={currencyCode} />

                <TooltipToggle className={element('price-info-toggle')} name="service-price-tooltip">
                  <Isvg src={ICONS.infoGrey} className={element('price-info-icon')} />
                </TooltipToggle>
                <TooltipContent hoverable name="service-price-tooltip">
                  <Message
                    message={messages.details.priceTooltip}
                    values={{
                      link: text => (
                        <a href={makePath.partnerTaxDefinition(locale)} target="_blank" rel="noopener noreferrer">
                          {text}
                        </a>
                      ),
                    }}
                  />
                </TooltipContent>
              </When>
            </div>
          </div>

          <When predicate={_isEmpty(practitioners) && _isEmpty(resources)}>
            <MissingObjectsCard
              titleMessage={messages.details.missingObjectsTitle}
              copyMessage={messages.details.missingObjectsCopy}
            />
            <hr />
          </When>

          <When predicate={!!description}>
            <div className={element('content', 'grid__item 1/1')}>{description}</div>
            <hr />
          </When>

          <Message as="h4" className="mt+" message={messages.details.availablePractitioners} />
          <div className="mb+">
            <When predicate={_isEmpty(practitioners)}>
              <Message
                message={messages.details.missingPractitionersLink}
                values={{
                  a: text => (
                    <Link to={makePath.manageServicesAdd({ venueId, tab: MANAGE_SERVICES_TAB.PRACTITIONERS })}>
                      {text}
                    </Link>
                  ),
                }}
              />
            </When>
            <When predicate={!_isEmpty(practitioners)}>
              {practitioners
                .map(({ firstName, lastName }) => (firstName ? `${firstName} ${lastName}` : lastName))
                .join(', ')}
            </When>
          </div>
          <hr />

          <Message as="h4" className="mt+" message={messages.details.availableEquipment} />

          <When predicate={_isEmpty(resources)}>
            <Message
              message={messages.details.missingEquipmentLink}
              values={{
                a: text => (
                  <Link to={makePath.manageServicesAdd({ venueId, tab: MANAGE_SERVICES_TAB.RESOURCES })}>{text}</Link>
                ),
              }}
            />
          </When>
          <When predicate={!_isEmpty(resources)}>
            {resources.map(({ name: serviceName }) => serviceName).join(', ')}
          </When>
        </div>
      )}
    </Card>
  );
}

export default ServiceDetails;
